import type { DocumentData } from 'firebase/firestore'

import {
  asIsoOrNull,
  asNumber,
  asOneOf,
  asRecord,
  asRecords,
  asString,
  asStrings,
  isRecord,
} from '@/repositories/setup-docs'
import { JOURNEY_PHASES } from '@/types/brand'
import {
  DIFFICULTIES,
  LEADS_TO_OFFER_LEVELS,
  PAGE_TYPES,
  PLAN_EDIT_STAGES,
  RELEVANCE_SCORES,
  TERM_SOURCES,
  type PlanEditStage,
  type PlanGateAnswer,
  type PlanStageReview,
  type PlanStepEdits,
  type ProposedPage,
  type ProposedTopic,
  type TopicCandidate,
  type TopicsGateAnswer,
} from '@/types/plan'

function asFiniteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function toTopicCandidate(data: DocumentData): TopicCandidate {
  return {
    id: asString(data.id),
    name: asString(data.name),
    whatItCovers: asString(data.whatItCovers),
    mainSearch: asString(data.mainSearch),
    volume: asNumber(data.volume),
    searches: asNumber(data.searches),
    keywordDifficulty: asFiniteOrNull(data.keywordDifficulty),
    difficulty: asOneOf(data.difficulty, DIFFICULTIES, 'medium'),
    arguments: asStrings(data.arguments),
    beliefSlugs: asStrings(data.beliefSlugs),
  }
}

function toTopicsAnswer(value: unknown): TopicsGateAnswer | null {
  if (!isRecord(value)) return null
  const answer = value
  return {
    kept: asRecords(answer.kept).map((topic) => ({
      candidateId: asString(topic.candidateId),
      name: asString(topic.name),
    })),
    added: asStrings(answer.added),
  }
}

function toPlanAnswer(value: unknown): PlanGateAnswer | null {
  return isRecord(value) ? { removedPageIds: asStrings(value.removedPageIds) } : null
}

function toProposedPage(data: DocumentData): ProposedPage {
  return {
    id: asString(data.id),
    question: asString(data.question),
    type: asOneOf(data.type, PAGE_TYPES, 'supporting'),
    stage: asOneOf(data.stage, JOURNEY_PHASES, 'awareness'),
    mainKeyword: typeof data.mainKeyword === 'string' ? data.mainKeyword : null,
    volume: asNumber(data.volume),
    updatesSitePage: data.updatesSitePage === true,
    decision: data.decision === true,
  }
}

function toProposedTopic(data: DocumentData): ProposedTopic {
  return {
    id: asString(data.id),
    name: asString(data.name),
    whatItCovers: asString(data.whatItCovers),
    hubPageId: asString(data.hubPageId),
    pages: asRecords(data.pages).map(toProposedPage),
  }
}

function toLines(data: DocumentData): readonly string[] {
  return asStrings(data.lines)
}

function toScore(value: unknown): (typeof RELEVANCE_SCORES)[number] {
  return RELEVANCE_SCORES.find((score) => score === value) ?? RELEVANCE_SCORES[0]
}

function toLeadsToOffer(value: unknown): (typeof LEADS_TO_OFFER_LEVELS)[number] {
  return LEADS_TO_OFFER_LEVELS.find((level) => level === value) ?? LEADS_TO_OFFER_LEVELS[0]
}

function toSearchedKeyword(row: DocumentData) {
  return {
    keyword: asString(row.keyword),
    volume: asNumber(row.volume),
    score: toScore(row.score),
    reason: asString(row.reason),
  }
}

/** Maps a `review/{stage}` doc of a plan run; `undefined` for a stage that has no review. */
export function toPlanStageReview(stage: string, data: DocumentData): PlanStageReview | undefined {
  const writtenAt = asIsoOrNull(data.writtenAt)
  const counts = asRecord(data.counts)
  const summary = { counts, lines: toLines(data), writtenAt }
  switch (stage) {
    case 'research':
      return {
        stage,
        coverage: asRecords(data.coverage).map((item) => ({
          id: asString(item.id),
          label: asString(item.label),
          terms: asRecords(item.terms).map((term) => ({
            term: asString(term.term),
            source: asOneOf(term.source, TERM_SOURCES, 'model'),
          })),
        })),
        writtenAt,
      }
    case 'competitors':
      return {
        stage,
        competitors: asRecords(data.competitors).map((rival) => ({
          name: asString(rival.name),
          website: asString(rival.website),
          searches: asNumber(rival.searches),
          dropped: rival.dropped === true,
        })),
        writtenAt,
      }
    case 'demand':
      return {
        stage,
        measured: asNumber(data.measured),
        perCoverage: asRecords(data.perCoverage).map((item) => ({
          id: asString(item.id),
          label: asString(item.label),
          terms: asNumber(item.terms),
          measured: asNumber(item.measured),
        })),
        writtenAt,
      }
    case 'relevance':
      return {
        stage,
        kept: asRecords(data.kept).map((row) => ({
          ...toSearchedKeyword(row),
          variants: asNumber(row.variants),
        })),
        dropped: asRecords(data.dropped).map(toSearchedKeyword),
        writtenAt,
      }
    case 'topic-candidates':
      return { stage, candidates: asRecords(data.candidates).map(toTopicCandidate), writtenAt }
    case 'review-topics':
      return { stage, answer: toTopicsAnswer(data.answer), writtenAt }
    case 'fill':
      return {
        stage,
        topics: asRecords(data.topics).map((topic) => ({
          name: asString(topic.name),
          terms: asNumber(topic.terms),
          measured: asNumber(topic.measured),
          kept: asNumber(topic.kept),
        })),
        writtenAt,
      }
    case 'serp':
      return {
        stage,
        ...summary,
        counts: {
          keywords: asNumber(counts.keywords),
          ordered: asNumber(counts.ordered),
          received: asNumber(counts.received),
          missing: asNumber(counts.missing),
          clusters: asNumber(counts.clusters),
        },
      }
    case 'grouping':
      return {
        stage,
        ...summary,
        counts: { keywords: asNumber(counts.keywords), pages: asNumber(counts.pages) },
      }
    case 'type-stage':
      return {
        stage,
        pages: asRecords(data.pages).map((page) => ({
          id: asString(page.id),
          question: asString(page.question),
          mainKeyword: asString(page.mainKeyword),
          keywords: asStrings(page.keywords),
          volume: asNumber(page.volume),
          type: asOneOf(page.type, PAGE_TYPES, 'supporting'),
          stage: asOneOf(page.stage, JOURNEY_PHASES, 'awareness'),
          leadsToOffer: toLeadsToOffer(page.leadsToOffer),
          canAnswer: page.canAnswer === true,
          reason: asString(page.reason),
        })),
        lines: toLines(data),
        writtenAt,
      }
    case 'placement':
      return {
        stage,
        ...summary,
        counts: {
          pages: asNumber(counts.pages),
          topics: asNumber(counts.topics),
          dropped: asNumber(counts.dropped),
        },
      }
    case 'decision-pages':
      return {
        stage,
        ...summary,
        counts: { pages: asNumber(counts.pages), objectives: asNumber(counts.objectives) },
      }
    case 'site-match':
      return {
        stage,
        ...summary,
        counts: {
          planned: asNumber(counts.planned),
          existing: asNumber(counts.existing),
          excluded: asNumber(counts.excluded),
          byRanking: asNumber(counts.byRanking),
          byModel: asNumber(counts.byModel),
          update: asNumber(counts.update),
        },
      }
    case 'review-plan':
      return {
        stage,
        proposal: { topics: asRecords(asRecord(data.proposal).topics).map(toProposedTopic) },
        answer: toPlanAnswer(data.answer),
        writtenAt,
      }
    case 'commit':
      return {
        stage,
        topics: asNumber(data.topics),
        pages: asNumber(data.pages),
        keywords: asNumber(data.keywords),
        writtenAt,
      }
    default:
      return undefined
  }
}

function isEditStage(value: string): value is PlanEditStage {
  return (PLAN_EDIT_STAGES as readonly string[]).includes(value)
}

/** Maps an `edits/{stage}` doc of a plan run; `undefined` for a stage that takes no edits. Only the edit groups the doc carries are set. */
export function toPlanStepEdits(stage: string, data: DocumentData): PlanStepEdits | undefined {
  if (!isEditStage(stage)) return undefined
  const terms = isRecord(data.terms) ? data.terms : null
  const competitors = isRecord(data.competitors) ? data.competitors : null
  const topics = isRecord(data.topics) ? data.topics : null
  return {
    stage,
    ...(terms
      ? { terms: { added: asStrings(terms.added), removed: asStrings(terms.removed) } }
      : {}),
    ...(competitors ? { competitors: { removed: asStrings(competitors.removed) } } : {}),
    ...(topics ? { topics: { added: asStrings(topics.added) } } : {}),
    updatedAt: asIsoOrNull(data.updatedAt),
  }
}
